import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tag, Button, Modal, Form, Input, Select, Switch, DatePicker, Alert, Spin, Space, Empty, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, NotificationOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import adminApi from '../../services/tenantService';
import { Announcement } from '../../types/tenant';

const { TextArea } = Input;

const dateFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const typeTagColors: Record<string, string> = {
  info: 'blue', warning: 'gold', maintenance: 'orange', feature: 'green', promotion: 'purple',
};

const typeOptions = [
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Maintenance', value: 'maintenance' },
  { label: 'Feature', value: 'feature' },
  { label: 'Promotion', value: 'promotion' },
];

const AdminAnnouncementsPage: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminApi.getAnnouncements({ limit: 50 });
      setAnnouncements(result.data);
      setTotal(result.total);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ type: 'info', isActive: true });
    setModalOpen(true);
  };

  const openEdit = (a: Announcement) => {
    setEditingId(a.id);
    form.setFieldsValue({
      title: a.title,
      content: a.content,
      type: a.type,
      isActive: a.isActive,
      startsAt: a.startsAt ? dayjs(a.startsAt) : undefined,
      expiresAt: a.expiresAt ? dayjs(a.expiresAt) : undefined,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        startsAt: values.startsAt ? values.startsAt.format('YYYY-MM-DD') : undefined,
        expiresAt: values.expiresAt ? values.expiresAt.format('YYYY-MM-DD') : undefined,
      };
      if (editingId) {
        await adminApi.updateAnnouncement(editingId, payload);
      } else {
        await adminApi.createAnnouncement(payload);
      }
      setModalOpen(false);
      fetchAnnouncements();
    } catch (err) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      setError((err as Error).message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminApi.deleteAnnouncement(id);
      fetchAnnouncements();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ color: '#999', fontSize: 14 }}>{total} announcements</span>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Announcement</Button>
      </div>

      {announcements.length === 0 ? (
        <Card>
          <Empty image={<NotificationOutlined style={{ fontSize: 48, color: '#ccc' }} />} description="No announcements yet" />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {announcements.map((a) => (
            <Card key={a.id} size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                  <Space style={{ marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{a.title}</span>
                    <Tag color={typeTagColors[a.type] || 'default'}>{a.type}</Tag>
                    {!a.isActive && <Tag>Inactive</Tag>}
                  </Space>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{a.content}</div>
                  <Space size={16} style={{ fontSize: 12, color: '#999' }}>
                    {a.createdByName && <span>By {a.createdByName}</span>}
                    <span>{dateFormat.format(new Date(a.createdAt))}</span>
                    {a.startsAt && <span>Starts: {dateFormat.format(new Date(a.startsAt))}</span>}
                    {a.expiresAt && <span>Expires: {dateFormat.format(new Date(a.expiresAt))}</span>}
                  </Space>
                </div>
                <Space>
                  <Button type="text" aria-label="Edit announcement" icon={<EditOutlined />} onClick={() => openEdit(a)} />
                  <Popconfirm title="Delete this announcement?" onConfirm={() => handleDelete(a.id)} okText="Delete" okButtonProps={{ danger: true }}>
                    <Button type="text" danger aria-label="Delete announcement" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title={editingId ? 'Edit Announcement' : 'New Announcement'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingId ? 'Update' : 'Create'}
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="content" label="Content" rules={[{ required: true, message: 'Content is required' }]}>
            <TextArea rows={4} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item name="type" label="Type">
              <Select options={typeOptions} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || document.body} />
            </Form.Item>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="startsAt" label="Starts At">
              <DatePicker style={{ width: '100%' }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || document.body} />
            </Form.Item>
            <Form.Item name="expiresAt" label="Expires At">
              <DatePicker style={{ width: '100%' }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || document.body} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminAnnouncementsPage;
